/**
 * StandardButton Component
 * 
 * Pre-configured button components for common use cases.
 * Use these instead of manually configuring Button props for consistency.
 */

import { Button, ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { hapticButton } from "@/utils/haptics";

// Primary Action Button (Submit, Create, Save)
export const PrimaryButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, onClick, ...props }, ref) => (
    <Button
      ref={ref}
      variant="default"
      className={cn("h-10 min-h-[44px]", className)}
      onClick={(e) => {
        hapticButton();
        onClick?.(e);
      }}
      {...props}
    />
  )
);
PrimaryButton.displayName = "PrimaryButton";

// Secondary Action Button (Cancel, Back)
export const SecondaryButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, onClick, ...props }, ref) => (
    <Button
      ref={ref}
      variant="outline"
      className={cn("h-10 min-h-[44px]", className)}
      onClick={(e) => {
        hapticButton();
        onClick?.(e);
      }}
      {...props}
    />
  )
);
SecondaryButton.displayName = "SecondaryButton";

// Destructive Action Button (Delete, Remove)
export const DestructiveButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, onClick, ...props }, ref) => (
    <Button
      ref={ref}
      variant="destructive"
      className={cn("h-10 min-h-[44px]", className)}
      onClick={(e) => {
        hapticButton();
        onClick?.(e);
      }}
      {...props}
    />
  )
);
DestructiveButton.displayName = "DestructiveButton";

// Icon Button (Settings, More Options)
export const IconButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="outline"
      size="icon"
      className={cn("h-10 w-10 min-h-[44px] min-w-[44px]", className)}
      {...props}
    />
  )
);
IconButton.displayName = "IconButton";

// Small Button (Compact areas, inline actions)
export const SmallButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "outline", ...props }, ref) => (
    <Button
      ref={ref}
      variant={variant}
      size="sm"
      className={cn("h-9", className)}
      {...props}
    />
  )
);
SmallButton.displayName = "SmallButton";

// Ghost Button (Filters, subtle actions)
export const GhostButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="ghost"
      className={cn("h-10 min-h-[44px]", className)}
      {...props}
    />
  )
);
GhostButton.displayName = "GhostButton";
